import type { ExportFormat } from '@/components/atoms/ExportButton/ExportButton';
import type { MetricExportEntry } from '@/modules/analytics/types';
import { trackAnalyticsExport } from '@/modules/analytics/utils/analyticsTracking';
import { flattenReportForCsv } from '@/modules/analytics/utils/buildReportExport';
import { PageName } from '@/shared/types/segment';
import { downloadCsv, downloadJson } from '@/util/fileManager';

type MetricsExportSpec = {
  /** Metrics to flatten for the CSV sheet (already filtered to those with data). */
  entries: MetricExportEntry[];
  filename: string;
  /** Segment event title — distinguishes whole-report from single-metric exports. */
  title: string;
  /** Analytics page the export was triggered from, for the tracking event. */
  page: PageName;
  /** Lazily builds the JSON payload (whole-report wrapper vs bare metric). */
  buildJson: () => unknown;
};

// Shared CSV/JSON dispatch for both the whole-report and single-metric export
// buttons. The only differences between them are the JSON shape, the filename,
// and the tracking title — everything else (format branch, tracking, the
// flatten→download tail) is identical.
export const downloadMetricsExport = (
  format: ExportFormat,
  { entries, filename, title, page, buildJson }: MetricsExportSpec
) => {
  if (entries.length === 0) {
    return;
  }
  trackAnalyticsExport(page, title, format);

  if (format === 'json') {
    downloadJson(buildJson(), filename);
    return;
  }

  const rows = flattenReportForCsv(entries);
  if (rows.length > 0) {
    downloadCsv(rows, filename);
  }
};
